import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../store/authStore'
import { billingApi } from '../api/client'
import { supportedLanguages } from '../i18n'
import { formatMoney, catalogPrice } from '../lib/pricing'
import { useProduct } from '../hooks/useCatalog'
import { useDisplayCurrency } from '../store/currencyStore'
import { useCompany } from '../hooks/useCompanyInfo'
import PaymentLogos from '../components/PaymentLogos'
// Landing styles live in web/src/marketing.css (scoped under `.lp`), bundled by
// Vite so they're present on first paint. Do NOT reintroduce a runtime <style>.

export default function LandingPage() {
  const { t, i18n } = useTranslation()
  // Legal pages are language-fixed by path: TR interface → Turkish slugs, everyone else → English.
  const isTR = i18n.language?.startsWith('tr')
  const legalPrivacyPath = isTR ? '/gizlilik-politikasi' : '/privacy'
  const legalTermsPath = isTR ? '/hizmet-sartlari' : '/terms'
  const legalKunyePath = isTR ? '/kunye' : '/legal-notice'
  const legalAboutPath = isTR ? '/hakkimizda' : '/about'
  const legalDistancePath = isTR ? '/mesafeli-satis-sozlesmesi' : '/distance-sales-agreement'
  const legalDeliveryPath = isTR ? '/teslimat-ve-iade' : '/delivery-and-returns'
  const productsPath = isTR ? '/urunler' : '/products'
  // Seller contact details in the footer, read from platform settings rather
  // than hardcoded. The legal name is deliberately NOT here: on a sole
  // proprietorship it is the owner's own name, and the footer is not the place
  // for it. The full identity lives on the künye and distance-sales pages,
  // which every footer links to.
  const company = useCompany()
  const companyLine = [
    company['company.address'],
    company['company.city'],
    company['company.phone'],
  ]
    .filter(Boolean)
    .join(' · ')
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const appName = 'Provisyer'
  const supportEmail: string | undefined = undefined
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [langOpen, setLangOpen] = useState(false)
  const langRef = useRef<HTMLDivElement>(null)
  const currentLang = supportedLanguages.find((l) => l.code === i18n.language) ?? supportedLanguages[0]

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code)
    setLangOpen(false)
    setMobileMenuOpen(false)
  }

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const { data: plansData } = useQuery({
    queryKey: ['billing-plans'],
    queryFn: billingApi.getPlans,
  })
  const freePlan = plansData?.plans.find((p) => p.id === 'free')
  const currency = useDisplayCurrency()
  // The price comes from the catalog, the table the checkout prices against.
  // It used to fall back to a hardcoded 1499/28 while the request was in
  // flight, which is how this page ended up quoting a number nobody updated.
  const slotProduct = useProduct('slot')
  const slotPrice = catalogPrice(slotProduct, 'monthly', currency)

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, navigate])

  // The chat widget (ai.sanalsantral.com.tr agent, lazily loaded after the
  // first interaction) is OFF for now. Restore the effect from git history to
  // bring it back. It was the only third-party script on the landing page, so
  // with it gone the page makes no external requests beyond the fonts.

  if (isAuthenticated) return null

  return (
    <div className="lp">
      <nav className="lp-nav">
        <Link to="/" className="lp-logo">provisyer</Link>
        <ul className="lp-nav-links">
          <li><a href="#features">{t('landing.nav.features')}</a></li>
          <li><Link to={productsPath}>{t('products.navProducts')}</Link></li>
          <li><a href="#pricing">{t('landing.nav.pricing')}</a></li>
          <li><Link to="/docs">{t('landing.nav.docs')}</Link></li>
        </ul>
        <div className="lp-nav-actions">
          <div className="lp-lang" ref={langRef}>
            <button
              className="lp-lang-btn"
              onClick={() => setLangOpen((o) => !o)}
              aria-label={t('landing.nav.language')}
              aria-expanded={langOpen}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
              <span>{currentLang.label}</span>
            </button>
            {langOpen && (
              <div className="lp-lang-menu">
                {supportedLanguages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={i18n.language === lang.code ? 'active' : ''}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <Link to="/register" className="lp-nav-cta">{t('landing.nav.getStarted')}</Link>
        </div>
        <button
          className="lp-nav-hamburger"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label={t('landing.nav.toggleMenu')}
        >
          <svg viewBox="0 0 24 24">
            {mobileMenuOpen ? (
              <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
            ) : (
              <><line x1="3" y1="7" x2="21" y2="7"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="17" x2="21" y2="17"/></>
            )}
          </svg>
        </button>
        {/* Mobile dropdown */}
        <div className={`lp-mobile-menu${mobileMenuOpen ? ' open' : ''}`}>
          <ul>
            <li><a href="#features" onClick={() => setMobileMenuOpen(false)}>{t('landing.nav.features')}</a></li>
            <li><a href="#pricing" onClick={() => setMobileMenuOpen(false)}>{t('landing.nav.pricing')}</a></li>
            <li><Link to="/docs" onClick={() => setMobileMenuOpen(false)}>{t('landing.nav.docs')}</Link></li>
          </ul>
          <Link to="/register" className="lp-mobile-cta" onClick={() => setMobileMenuOpen(false)}>
            {t('landing.nav.getStarted')}
          </Link>
          <div className="lp-mobile-langs">
            {supportedLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className={i18n.language === lang.code ? 'active' : ''}
              >
                {lang.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      <section style={{ padding: '0 40px' }}>
        <div className="lp-hero">
          <div className="lp-hero-eyebrow">{t('landing.hero.eyebrow')}</div>
          <h1>{t('landing.hero.title').split('\n').map((line, i, arr) => {
            if (line === t('landing.hero.titleEmphasis')) return <em key={i}>{line}</em>
            return <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
          })}</h1>
          <p>{t('landing.hero.subtitle')}</p>
          <div className="lp-hero-actions">
            <Link to="/register" className="lp-btn-primary">{t('landing.hero.cta')} <span>↗</span></Link>
            <a href="#features" className="lp-btn-secondary">{t('landing.hero.secondaryCta')}</a>
          </div>
        </div>
      </section>

      <div className="lp-stats">
        <div className="lp-stat">
          <div className="lp-stat-num">3<span>{t('landing.stats.minUnit')}</span></div>
          <div className="lp-stat-label">{t('landing.stats.minLabel').split('\n').map((line, i) => <span key={i}>{line}{i === 0 && <br />}</span>)}</div>
        </div>
        <div className="lp-stat">
          <div className="lp-stat-num">99<span>{t('landing.stats.uptimeUnit')}</span></div>
          <div className="lp-stat-label">{t('landing.stats.uptimeLabel').split('\n').map((line, i) => <span key={i}>{line}{i === 0 && <br />}</span>)}</div>
        </div>
        <div className="lp-stat">
          <div className="lp-stat-num">v<span>{t('landing.stats.versionsUnit')}</span></div>
          <div className="lp-stat-label">{t('landing.stats.versionsLabel').split('\n').map((line, i) => <span key={i}>{line}{i === 0 && <br />}</span>)}</div>
        </div>
      </div>

      {/* MCP: the headline differentiator — drive your Odoo fleet from Claude */}
      <section className="lp-mcp">
        <div className="lp-mcp-inner">
          <div className="lp-mcp-copy">
            <div className="lp-mcp-eyebrow">
              <span>◆</span> {t('landing.mcp.eyebrowPre')} <b>MCP</b> {t('landing.mcp.eyebrowPost')}
            </div>
            <h2>{t('landing.mcp.titlePre')} <em>{t('landing.mcp.titleEm')}</em></h2>
            <p className="lp-mcp-sub">
              {t('landing.mcp.sub')}
            </p>
            <ul className="lp-mcp-caps">
              <li><span><b>{t('landing.mcp.cap1Title')}</b> - {t('landing.mcp.cap1Desc')}</span></li>
              <li><span><b>{t('landing.mcp.cap2Title')}</b> - {t('landing.mcp.cap2Desc')}</span></li>
              <li><span><b>{t('landing.mcp.cap3Title')}</b> - {t('landing.mcp.cap3DescPre')} <code>git push</code>{t('landing.mcp.cap3DescPost')}</span></li>
              <li><span><b>{t('landing.mcp.cap4Title')}</b> - {t('landing.mcp.cap4Desc')}</span></li>
              <li><span><b>{t('landing.mcp.cap5Title')}</b> - {t('landing.mcp.cap5Desc')}</span></li>
            </ul>
            <p className="lp-mcp-clients">
              {t('landing.mcp.clientsPre')} <b>Claude Desktop</b>, <b>Claude Code</b>, {t('landing.mcp.clientsAnd')} · stdio transport · {t('landing.mcp.clientsScoped')}
            </p>
          </div>

          <div className="lp-chat" role="img" aria-label={t('landing.mcp.chat.ariaLabel')}>
            <div className="lp-chat-bar">
              <div className="dot" style={{ background: '#ff5f56' }}></div>
              <div className="dot" style={{ background: '#ffbd2e' }}></div>
              <div className="dot" style={{ background: '#27c93f' }}></div>
              <span className="lp-chat-title">claude · provisyer-mcp</span>
            </div>
            <div className="lp-chat-body">
              <div className="lp-msg">
                <div className="lp-msg-role" style={{ textAlign: 'right' }}>{t('landing.mcp.chat.you')}</div>
                <div className="lp-bubble-user">
                  {t('landing.mcp.chat.userPre')} <b>customer-acme</b> {t('landing.mcp.chat.userPost')}
                </div>
              </div>
              <div className="lp-msg">
                <div className="lp-msg-role">Claude</div>
                <div className="lp-bubble-ai">
                  <div className="lp-tools-wrap">
                    <div className="lp-tool">
                      <span className="tname">get_instance</span>
                      <span className="targ">"customer-acme"</span>
                      <span className="tok">✓</span>
                    </div>
                    <div className="lp-tool">
                      <span className="tname">install_from_github</span>
                      <span className="targ">github.com/acme/odoo-helpdesk</span>
                      <span className="tok">✓</span>
                    </div>
                  </div>
                  <div className="lp-ai-final">
                    {t('landing.mcp.chat.finalDone')} <b>helpdesk</b> {t('landing.mcp.chat.finalInstalledOn')} <b>customer-acme</b> {t('landing.mcp.chat.finalJob')} <span className="ok">{t('landing.mcp.chat.finalOk')}</span>{t('landing.mcp.chat.finalEnd')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div id="features" className="lp-features">
        <div className="lp-feat">
          <div className="lp-feat-tag">{t('landing.features.infrastructure.tag')}</div>
          <div className="lp-feat-icon">⚡</div>
          <h3>{t('landing.features.infrastructure.title')}</h3>
          <p>{t('landing.features.infrastructure.desc')}</p>
        </div>
        <div className="lp-feat">
          <div className="lp-feat-tag">{t('landing.features.security.tag')}</div>
          <div className="lp-feat-icon">🔒</div>
          <h3>{t('landing.features.security.title')}</h3>
          <p>{t('landing.features.security.desc')}</p>
        </div>
        <div className="lp-feat">
          <div className="lp-feat-tag">{t('landing.features.customisation.tag')}</div>
          <div className="lp-feat-icon">🧩</div>
          <h3>{t('landing.features.customisation.title')}</h3>
          <p>{t('landing.features.customisation.desc')}</p>
        </div>
        <div className="lp-feat">
          <div className="lp-feat-tag">{t('landing.features.observability.tag')}</div>
          <div className="lp-feat-icon">📡</div>
          <h3>{t('landing.features.observability.title')}</h3>
          <p>{t('landing.features.observability.desc')}</p>
        </div>
        <div className="lp-feat">
          <div className="lp-feat-tag">{t('landing.features.reliability.tag')}</div>
          <div className="lp-feat-icon">🔄</div>
          <h3>{t('landing.features.reliability.title')}</h3>
          <p>{t('landing.features.reliability.desc', { appName })}</p>
        </div>
        <div className="lp-feat">
          <div className="lp-feat-tag">{t('landing.features.modules.tag')}</div>
          <div className="lp-feat-icon">📦</div>
          <h3>{t('landing.features.modules.title')}</h3>
          <p>{t('landing.features.modules.desc')}</p>
        </div>
      </div>

      <div id="pricing" className="lp-pricing">
        <div className="lp-section-label">{t('landing.pricing.sectionLabel')}</div>
        <div className="lp-plans">
          {/* Free */}
          <div className="lp-plan">
            <div className="lp-plan-name">{freePlan?.name ?? t('pricing.freeName')}</div>
            <div className="lp-plan-price">{formatMoney(0, currency, i18n.language)}<sub>/{t('pricing.instancePerMo')}</sub></div>
            <ul className="lp-plan-features" style={{ marginTop: '24px' }}>
              {(freePlan?.features ?? []).map((f) => <li key={f}>{f}</li>)}
            </ul>
            <Link to="/register" className="lp-plan-btn">
              {t('landing.pricing.getStartedFree')}
            </Link>
          </div>
          {/* Paid (per-instance) */}
          <div className="lp-plan featured">
            <div className="lp-plan-name">{t('pricing.paidName')}</div>
            <div className="lp-plan-price">{formatMoney(slotPrice, currency, i18n.language)}<sub>/{t('pricing.instancePerMo')}</sub></div>
            <ul className="lp-plan-features" style={{ marginTop: '24px' }}>
              <li>{t('pricing.paidFeatureCustomDomain')}</li>
              <li>{t('pricing.paidFeatureCustomAddons')}</li>
              <li>{t('pricing.paidFeatureBackups')}</li>
              <li>{t('pricing.paidFeatureScripts')}</li>
              <li>{t('pricing.paidFeatureClone')}</li>
              <li>{t('pricing.paidFeatureLargeStorage')}</li>
            </ul>
            <Link to="/register" className="lp-plan-btn">
              {t('landing.pricing.getStarted')}
            </Link>
          </div>
          {/* Enterprise */}
          <div className="lp-plan lp-plan-enterprise">
            <div className="lp-plan-name">{t('landing.pricing.enterpriseName')}</div>
            <div className="lp-plan-price lp-plan-price-contact">{t('landing.pricing.enterprisePrice')}</div>
            <div className="lp-plan-yearly-note">{t('landing.pricing.enterprisePriceNote')}</div>
            <ul className="lp-plan-features" style={{ marginTop: '24px' }}>
              <li>{t('pricing.enterpriseFeature247Support')}</li>
              <li>{t('pricing.enterpriseFeatureSysAdmin')}</li>
              <li>{t('pricing.enterpriseFeatureUnlimitedResources')}</li>
              <li>{t('pricing.enterpriseFeatureCustomModules')}</li>
              <li>{t('pricing.enterpriseFeatureSLA')}</li>
              <li>{t('pricing.enterpriseFeatureDedicatedManager')}</li>
              <li>{t('pricing.enterpriseFeatureOnPremise')}</li>
              <li>{t('pricing.enterpriseFeatureTraining')}</li>
            </ul>
            <a href={supportEmail ? `mailto:${supportEmail}` : 'mailto:info@provisyer.com'} className="lp-plan-btn lp-plan-btn-enterprise">
              {t('landing.pricing.contactSales')}
            </a>
          </div>
        </div>
        <p className="lp-pricing-note">
          {t('landing.pricing.note')}
        </p>
      </div>

      <footer className="lp-footer">
        <Link to="/" className="lp-footer-logo">{appName.toLowerCase()}</Link>
        <ul className="lp-footer-links">
          <li><Link to={productsPath}>{t('products.navProducts')}</Link></li>
          <li><Link to={legalAboutPath}>{t('footer.about')}</Link></li>
          <li><Link to={legalDistancePath}>{t('footer.distanceSales')}</Link></li>
          <li><Link to={legalDeliveryPath}>{t('footer.deliveryReturn')}</Link></li>
          <li><Link to={legalPrivacyPath}>{t('landing.footer.privacyPolicy')}</Link></li>
          <li><Link to={legalTermsPath}>{t('landing.footer.termsOfService')}</Link></li>
          <li><Link to={legalKunyePath}>{t('landing.footer.kunye')}</Link></li>
        </ul>
        <div className="lp-footer-note">
          <a href="https://simplstack.com" target="_blank" rel="noopener noreferrer">
            <svg className="ss-icon" width="18" height="16" viewBox="0 0 168 160" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g><rect x="4" width="108" height="108" fill="url(#ss0)"/></g>
              <g><rect x="20" y="16" width="108" height="108" fill="url(#ss1)"/></g>
              <g><rect x="36" y="32" width="108" height="108" fill="url(#ss2)"/></g>
              <defs>
                <linearGradient id="ss0" x1="58" y1="0" x2="58" y2="108" gradientUnits="userSpaceOnUse"><stop stopColor="#51DAED"/><stop offset=".35" stopColor="#3DC3D6"/><stop offset=".9" stopColor="#3390C4"/></linearGradient>
                <linearGradient id="ss1" x1="74" y1="16" x2="74" y2="124" gradientUnits="userSpaceOnUse"><stop stopColor="#51DAED"/><stop offset=".35" stopColor="#3DC3D6"/><stop offset=".9" stopColor="#3390C4"/></linearGradient>
                <linearGradient id="ss2" x1="90" y1="32" x2="90" y2="140" gradientUnits="userSpaceOnUse"><stop stopColor="#51DAED"/><stop offset=".35" stopColor="#3DC3D6"/><stop offset=".9" stopColor="#3390C4"/></linearGradient>
              </defs>
            </svg>
          </a>
          {t('landing.footer.copyright', { year: new Date().getFullYear(), appName })}
          {companyLine && <><br />{companyLine}</>}
        </div>
        <PaymentLogos size="h-6" />
      </footer>
    </div>
  )
}
